// Automation-related filters

import {
	createDateRangeFilter,
	createKeywordFilter,
	createStatusFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const automationFilters: FilterRegistry = {
	automationKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by name or description...',
			objectTable: '_automation_flow',
			listKeys: ['automation_flows'],
			fulltextFields: ['name', 'description'],
		}),
		queryParamName: 'automationKeywords',
	},

	automationStatus: {
		...createStatusFilter({
			label: 'Flow Status',
			options: [
				{ id: 'draft', label: 'Draft' },
				{ id: 'active', label: 'Active' },
				{ id: 'paused', label: 'Paused' },
				{ id: 'completed', label: 'Completed' },
			],
			objectTable: '_automation_flow',
			fieldName: 'status',
			listKeys: ['automation_flows'],
		}),
		queryParamName: 'automationStatus',
	},

	automationCreatedDate: {
		...createDateRangeFilter({
			label: 'Created Date',
			objectTable: '_automation_flow',
			dateField: 'createdDate',
			listKeys: ['automation_flows'],
		}),
		queryParamName: 'automationCreatedDate',
	},

	automationModifiedDate: {
		...createDateRangeFilter({
			label: 'Modified Date',
			objectTable: '_automation_flow',
			dateField: 'modifiedDate',
			listKeys: ['automation_flows'],
		}),
		queryParamName: 'automationModifiedDate',
		colorScheme: 'green',
	},

	automationBatchStatus: {
		...createStatusFilter({
			label: 'Batch Status',
			options: [
				{ id: 'pending', label: 'Pending' },
				{ id: 'scheduled', label: 'Scheduled' },
				{ id: 'running', label: 'Running' },
				{ id: 'completed', label: 'Completed' },
				{ id: 'failed', label: 'Failed' },
				{ id: 'cancelled', label: 'Cancelled' },
				{ id: 'recurring', label: 'Recurring' },
			],
			objectTable: '_automation_batch',
			fieldName: 'batchStatus',
			listKeys: ['automation_batches'],
			colorScheme: 'orange',
		}),
		queryParamName: 'automationBatchStatus',
	},

	automationBatchParentId: {
		type: 'single-select',
		label: 'Parent Batch',
		placeholder: 'Select parent batch filter...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'automationBatchParentId',
		multiple: false,
		options: [
			{ id: 'All', label: 'All' },
			{ id: 'top-level', label: 'Top-level only' },
			{ id: 'child', label: 'Child batches only' },
		],
		queryMapping: {
			objectTable: '_automation_batch',
			fieldName: 'parentBatchId',
			joinType: 'direct',
		},
		format: 'single',
		listKeys: ['automation_batches'],
		valueExtractor: {
			property: 'id',
		},
	},

	automationBatchKeywords: {
		...createKeywordFilter({
			label: 'Search Batches',
			placeholder: 'Search by name or description...',
			objectTable: '_automation_batch',
			listKeys: ['automation_batches'],
			fulltextFields: ['name', 'description'],
		}),
		queryParamName: 'automationBatchKeywords',
	},

	automationBatchCreatedDate: {
		...createDateRangeFilter({
			label: 'Created Date',
			objectTable: '_automation_batch',
			dateField: 'createdDate',
			listKeys: ['automation_batches'],
		}),
		queryParamName: 'automationBatchCreatedDate',
	},

	automationTargetKeywords: {
		...createKeywordFilter({
			label: 'Search Target Lists',
			placeholder: 'Search by name or description...',
			objectTable: '_automation_target_list',
			listKeys: ['automation_target_lists', 'automation-target-lists-embedded'],
			fulltextFields: ['name', 'description'],
		}),
		queryParamName: 'automationTargetKeywords',
	},

	automationTargetType: {
		...createStatusFilter({
			label: 'Entity Type',
			options: [
				{ id: 'client', label: 'Client' },
				{ id: 'contact', label: 'Contact' },
				{ id: 'order', label: 'Order' },
				{ id: 'invoice', label: 'Invoice' },
				{ id: 'lead', label: 'Lead' },
			],
			objectTable: '_automation_target_list',
			fieldName: 'entityType',
			listKeys: ['automation_target_lists', 'automation-target-lists-embedded'],
			colorScheme: 'purple',
		}),
		queryParamName: 'automationTargetType',
	},

	automationTargetCreatedDate: {
		...createDateRangeFilter({
			label: 'Created Date',
			objectTable: '_automation_target_list',
			dateField: 'createdDate',
			listKeys: ['automation_target_lists', 'automation-target-lists-embedded'],
		}),
		queryParamName: 'automationTargetCreatedDate',
	},

	automationExecutionReportKeywords: {
		...createKeywordFilter({
			label: 'Search Reports',
			placeholder: 'Search by batch name or flow name...',
			objectTable: '_automation_execution_report',
			listKeys: [
				'automation_reports',
				'automation-reports-batch-embedded',
				'automation-reports-trigger-embedded',
			],
			fulltextFields: ['flowName', 'batchName'],
		}),
		queryParamName: 'automationExecutionReportKeywords',
	},

	automationExecutionReportStatus: {
		...createStatusFilter({
			label: 'Execution Status',
			options: [
				{ id: 'pending', label: 'Pending' },
				{ id: 'running', label: 'Running' },
				{ id: 'completed', label: 'Completed' },
				{ id: 'failed', label: 'Failed' },
				{ id: 'cancelled', label: 'Cancelled' },
			],
			objectTable: '_automation_execution_report',
			fieldName: 'executionStatus',
			listKeys: [
				'automation_reports',
				'automation-reports-batch-embedded',
				'automation-reports-trigger-embedded',
			],
			colorScheme: 'green',
		}),
		queryParamName: 'automationExecutionReportStatus',
	},

	automationExecutionReportCreatedDate: {
		...createDateRangeFilter({
			label: 'Execution Date',
			objectTable: '_automation_execution_report',
			dateField: 'startedAt',
			listKeys: [
				'automation_reports',
				'automation-reports-batch-embedded',
				'automation-reports-trigger-embedded',
			],
		}),
		queryParamName: 'automationExecutionReportCreatedDate',
	},

	automationReportBatch: {
		type: 'multi-select',
		label: 'Batch',
		placeholder: 'Filter by batch...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'automationReportBatch',
		multiple: true,
		queryMapping: {
			objectTable: '_automation_execution_report',
			fieldName: 'batchId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['automation_reports'],
	},

	automationTriggerKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by name or description...',
			objectTable: 'automation_trigger',
			listKeys: ['automation_trigger'],
			fulltextFields: ['name', 'description'],
		}),
		queryParamName: 'automationTriggerKeywords',
	},

	automationTriggerType: {
		...createStatusFilter({
			label: 'Trigger Type',
			options: [
				{ id: 'event', label: 'Event' },
				{ id: 'scheduled', label: 'Scheduled' },
				{ id: 'api', label: 'API' },
				{ id: 'manual', label: 'Manual' },
			],
			objectTable: 'automation_trigger',
			fieldName: 'triggerType',
			listKeys: ['automation_trigger'],
		}),
		queryParamName: 'automationTriggerType',
	},

	automationTriggerStatus: {
		...createStatusFilter({
			label: 'Status',
			options: [
				{ id: 'true', label: 'Active' },
				{ id: 'false', label: 'Inactive' },
			],
			objectTable: 'automation_trigger',
			fieldName: 'isActive',
			listKeys: ['automation_trigger'],
			colorScheme: 'green',
		}),
		queryParamName: 'automationTriggerStatus',
	},
};
